import { Button, cn } from '@llura/ui';

interface SegmentedPickerProps<T extends string | number> {
  options: readonly T[];
  value: T | null;
  onChange: (next: T) => void;
  labelFor: (option: T) => string;
  /** Optional aria-label for the surrounding group (sets `role="radiogroup"`). */
  ariaLabel?: string;
}

/**
 * Compact 2-to-N chip picker shared by every enum field on the Your-personality
 * screen (boundary levels, gender, tone, intensity, period, locale). Each
 * option renders as a Button; the active option gets `bg-primary` to make
 * tap targets unambiguous on phones.
 */
export function SegmentedPicker<T extends string | number>({
  options,
  value,
  onChange,
  labelFor,
  ariaLabel,
}: SegmentedPickerProps<T>) {
  return (
    <div
      role={ariaLabel ? 'radiogroup' : undefined}
      aria-label={ariaLabel}
      className="grid w-full gap-2"
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      {options.map((option) => {
        const active = option === value;
        return (
          <Button
            key={String(option)}
            type="button"
            variant="outline"
            size="sm"
            role={ariaLabel ? 'radio' : undefined}
            aria-checked={ariaLabel ? active : undefined}
            onClick={() => onChange(option)}
            className={cn(
              'h-10 rounded-full border-border text-sm transition-colors',
              active
                ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground'
                : 'text-foreground/80 hover:bg-muted',
            )}
          >
            {labelFor(option)}
          </Button>
        );
      })}
    </div>
  );
}
